import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import { FormModal, Field, TextArea, SelectField } from "./LeadsPage";
import { Plus, Pencil, Trash2, ArrowRight, Send, Wrench, Bot, Edit3, MapPin, Info, ArrowUpDown, CalendarClock, X, Check, Copy, Clock3, Lock, Search, Mail, Archive as ArchiveIcon, Users, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ARCHIVE_GROUPS, matchesArchiveQuery } from "./ArchivePage";
import { toast } from "sonner";
import { useBilling } from "../context/BillingContext";
import SendQuoteModal from "../components/SendQuoteModal";
import QuoteReviewModal from "../components/QuoteReviewModal";
import ConvertToJobModal from "../components/ConvertToJobModal";
import ContactPickerField from "../components/ContactPickerField";
import DetailsModal, { CustomerVehiclePhoto } from "../components/DetailsModal";
import StatusPipeline from "../components/StatusPipeline";
import JobTypeMultiSelect from "../components/JobTypeMultiSelect";
import JobItemForm from "../components/JobItemForm";
import { LOCATION_TYPES, jobTypeMeta, blankJobItem, normalizeItem, serviceTypesLabel } from "../lib/jobTypes";
import { SUB_STATUSES } from "../lib/pipeline";

const STATUS_PILL = {
  unquoted: "pill-error",
  draft: "pill-muted",
  sent: "pill-info",
  accepted: "pill-success",
  rejected: "pill-error",
};

// Comparator functions for each sort option. Keyed by option value so the
// dropdown can look up the right one without a switch statement.
const SORT_OPTIONS = [
  { value: "arrived_desc", label: "Newest into Quotes (default)" },
  { value: "arrived_asc",  label: "Oldest into Quotes first" },
  { value: "amount_desc",  label: "Amount — high to low" },
  { value: "amount_asc",   label: "Amount — low to high" },
  { value: "customer_asc", label: "Customer name — A to Z" },
  { value: "customer_desc",label: "Customer name — Z to A" },
  { value: "updated_desc", label: "Recently updated" },
];

const _arrivedKey = (q) => q.quote_arrived_at || q.created_at || "";
const _amountKey  = (q) => Number(q.amount) || 0;
const _customerKey = (q) => (q.contact_name || "").toLowerCase();
const _updatedKey = (q) => q.updated_at || q.created_at || "";

// Follow-up prompt helper — figures out which (if any) manual follow-up
// prompts to render on a sent quote. Returns the first step whose due
// time has passed AND hasn't already been sent or skipped. Only steps 1
// (3-day) and 2 (7-day) surface as UI prompts — step 3 (14-day) stays
// on the auto-sweep so a lingering quote still gets one closing nudge.
const FOLLOWUP_STEP_META = {
  1: { days: 3,  label: "3-day"  },
  2: { days: 7,  label: "7-day"  },
};
function nextFollowupPrompt(q) {
  const sub = q.sub_status || "";
  if (sub !== "sent" && sub !== "follow_up") return null;
  const fs = q.followup_state || {};
  if (!fs.enabled) return null;
  const sentSteps = Array.isArray(fs.sent_steps) ? fs.sent_steps : [];
  const skippedSteps = Array.isArray(fs.skipped_steps) ? fs.skipped_steps : [];
  // Anchor on when the first send actually happened. Falls back to
  // `started_at`, then `quote_arrived_at`, then `created_at` so legacy
  // records with partial state still render a sensible countdown.
  const startRaw = fs.started_at || q.quote_arrived_at || q.created_at;
  if (!startRaw) return null;
  const startTs = new Date(startRaw).getTime();
  if (!Number.isFinite(startTs)) return null;
  const nowTs = Date.now();
  const daysSinceSent = (nowTs - startTs) / 86400000;
  for (const step of [1, 2]) {
    if (sentSteps.includes(step)) continue;
    if (skippedSteps.includes(step)) continue;
    const meta = FOLLOWUP_STEP_META[step];
    if (daysSinceSent >= meta.days) {
      return { step, label: meta.label, days: meta.days, daysSince: Math.floor(daysSinceSent) };
    }
  }
  return null;
}

// Quotes have a 30-day expiry window from when they land. Returns an
// integer number of days remaining (negative when expired), or null if
// the arrival date is missing so the caller can hide the pill.
const QUOTE_EXPIRY_DAYS = 30;
function quoteExpiryDaysLeft(q) {
  const anchor = q.quote_arrived_at || q.created_at;
  if (!anchor) return null;
  const t = new Date(anchor).getTime();
  if (!Number.isFinite(t)) return null;
  const expiresMs = t + QUOTE_EXPIRY_DAYS * 86400000;
  return Math.ceil((expiresMs - Date.now()) / 86400000);
}

const SORT_COMPARATORS = {
  arrived_desc: (a, b) => _arrivedKey(b).localeCompare(_arrivedKey(a)),
  arrived_asc:  (a, b) => _arrivedKey(a).localeCompare(_arrivedKey(b)),
  amount_desc:  (a, b) => _amountKey(b) - _amountKey(a),
  amount_asc:   (a, b) => _amountKey(a) - _amountKey(b),
  customer_asc: (a, b) => _customerKey(a).localeCompare(_customerKey(b)),
  customer_desc:(a, b) => _customerKey(b).localeCompare(_customerKey(a)),
  updated_desc: (a, b) => _updatedKey(b).localeCompare(_updatedKey(a)),
};

const blank = {
  contact_id: "",
  contact_name: "", contact_email: "", contact_phone: "", contact_address: "",
  vehicle: "", service: "", amount: 0, notes: "",
  main_status: "quote", sub_status: "draft",
  job_type: "car", location_type: "on_site", location_address: "",
  job_items: [],
  amount_override: 0,
  is_estimate: false,
  // Customer-facing install duration — free text so office can write
  // "2 business days", "4-6 hours", "Approx. 3 days". Separate from the
  // internal `estimated_hours` per installation (which is for payroll /
  // scheduling, not customer comms).
  customer_install_duration: "",
};

/**
 * FollowUpPill — a sent-quote-only prompt that nudges the office to send
 * a 3-day (step 1) or 7-day (step 2) follow-up email. Appears inline in
 * the quote's status row the moment that step becomes due. Clicking the
 * primary action fires the email via `/followups/send-now`; the small
 * secondary "skip" button advances to the next step without sending.
 * Reloads the parent list on success so the pill disappears/updates.
 */
function FollowUpPill({ quote, onDone }) {
  const prompt = React.useMemo(() => nextFollowupPrompt(quote), [quote]);
  const [busy, setBusy] = React.useState(false);
  if (!prompt) return null;

  const sendNow = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/quotes/${quote.id}/followups/send-now`,
        null,
        { params: { step: prompt.step } },
      );
      if (data.result?.sent) {
        toast.success(`${prompt.label} follow-up sent`);
      } else {
        toast.error(data.result?.reason
          ? `Follow-up not sent: ${data.result.reason}`
          : "Follow-up could not be sent");
      }
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send follow-up");
    } finally {
      setBusy(false);
    }
  };

  const skip = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(
        `/quotes/${quote.id}/followups/skip-step`,
        null,
        { params: { step: prompt.step } },
      );
      toast.info(`${prompt.label} prompt dismissed`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to dismiss");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span
      className="inline-flex items-center gap-1 shrink-0 pill-info pill animate-[pulse_2.4s_ease-in-out_infinite] gap-1 border border-sky-400/50 bg-sky-500/15"
      data-testid={`quote-followup-prompt-${quote.id}`}
      data-step={prompt.step}
      onClick={(e) => e.stopPropagation()}
      title={`Quote was sent ${prompt.daysSince} day${prompt.daysSince === 1 ? "" : "s"} ago — send the ${prompt.label} follow-up?`}
    >
      <Mail className="w-2.5 h-2.5" />
      <button
        type="button"
        onClick={sendNow}
        disabled={busy}
        data-testid={`quote-followup-send-${quote.id}`}
        className="font-semibold uppercase tracking-wider text-[10px] hover:underline disabled:opacity-50"
      >
        {busy ? "Sending…" : `Send ${prompt.label} follow-up?`}
      </button>
      <button
        type="button"
        onClick={skip}
        disabled={busy}
        data-testid={`quote-followup-skip-${quote.id}`}
        className="ml-0.5 opacity-70 hover:opacity-100 hover:text-slate-100 text-[10px] px-1 border-l border-sky-400/40 pl-1.5"
        title="Dismiss this prompt without sending"
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </span>
  );
}

export default function QuotesPage() {
  const navigate = useNavigate();
  const { billing } = useBilling();
  // Automatic AI Quoting is bundled with the Pro plan. For Starter shops
  // we swap the "Auto quote" button for a locked-upgrade CTA so they see
  // the feature exists but understand what unlocks it.
  const isProPlan = (billing?.plan || "").toLowerCase() === "pro";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [sending, setSending] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [convertingToJob, setConvertingToJob] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem("wc_quotes_sort") || "arrived_desc"; }
    catch (_) { return "arrived_desc"; }
  });
  // Free-text search across contact / vehicle / service / amount — not
  // persisted; it's a per-session filter, not a saved preference.
  const [query, setQuery] = useState("");

  // Lazily-loaded archive rows so the Quotes-tab search can surface a
  // "found in archive" pill. We only hit `/api/jobs?main_status=archived`
  // once the user actually starts typing — most sessions never search, so
  // this keeps the Quotes tab snappy for the common case.
  const [archiveRows, setArchiveRows] = useState([]);
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  useEffect(() => {
    if (!query.trim() || archiveLoaded) return;
    (async () => {
      try {
        const { data } = await api.get("/jobs", { params: { main_status: "archived" } });
        setArchiveRows(Array.isArray(data) ? data : []);
      } catch (_) {
        setArchiveRows([]);
      } finally {
        setArchiveLoaded(true);
      }
    })();
  }, [query, archiveLoaded]);

  // Bucket archive matches by the same (jobs / quotes / leads) rules
  // ArchivePage uses so the pill labels line up exactly with what the
  // user will see after clicking through.
  const archiveMatches = useMemo(() => {
    const needle = (query || "").trim();
    if (!needle || !archiveRows.length) return null;
    const buckets = { jobs: 0, quotes: 0, leads: 0, total: 0 };
    for (const r of archiveRows) {
      if (!matchesArchiveQuery(r, needle)) continue;
      const sub = (r.sub_status || "").toLowerCase();
      const hit = ARCHIVE_GROUPS.find((g) => g.subs.has(sub));
      if (!hit) continue; // skip "other" so counts stay predictable
      buckets[hit.key] += 1;
      buckets.total += 1;
    }
    return buckets.total ? buckets : null;
  }, [archiveRows, query]);

  const load = async () => {
    const { data } = await api.get("/jobs", { params: { main_status: "quote" } });
    // Raw fetch — sorting is applied client-side via `sortedItems` so the
    // dropdown can re-order without a network round-trip.
    setItems(data);
  };

  // Persist the admin's preferred sort so it survives reloads.
  useEffect(() => {
    try { localStorage.setItem("wc_quotes_sort", sortBy); } catch (_) { /* noop */ }
  }, [sortBy]);

  const sortedItems = useMemo(() => {
    const cmp = SORT_COMPARATORS[sortBy] || SORT_COMPARATORS.arrived_desc;
    const sorted = [...items].sort(cmp);
    // Apply the free-text search filter. Matches on contact name, email,
    // phone, vehicle string, service string, status pill label, and
    // amount so a search for either "golf", "0400 123" or "4500" lands
    // the right row without the user having to think about which column
    // the data lives in.
    const needle = (query || "").trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((it) => {
      const hay = [
        it.contact_name, it.contact_email, it.contact_phone,
        it.vehicle, it.service,
        it.sub_status, it.main_status,
        (it.amount != null ? String(it.amount) : ""),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [items, sortBy, query]);

  useEffect(() => { load(); }, []);

  // Detection-based auto-quote sweep: any quote card sitting unquoted with
  // no prior auto-quote attempt gets kicked off exactly once per page load.
  // This replaces the write-time trigger so quotes imported / seeded / left
  // behind also get priced without the office having to click "Auto".
  const kickedRef = useRef(new Set());
  useEffect(() => {
    if (!items?.length) return;
    const candidates = items.filter((it) => (
      it.main_status === "quote"
      && (Number(it.amount) || 0) <= 0
      && !it.auto_quoted_at
      && !it.auto_quoting_pending
      && !it.auto_quote_error   // don't retry failed ones; user can hit Review quote
      && !kickedRef.current.has(it.id)
    ));
    if (!candidates.length) return;
    (async () => {
      for (const q of candidates) {
        kickedRef.current.add(q.id);
        try { await api.post(`/quote-agent/auto-quote/${q.id}`); } catch { /* ignore */ }
      }
      // Refresh once so the pending pills light up on all affected rows
      load();
    })();
  }, [items]);

  // While any quote is being auto-quoted by the AI, poll the list every 3s
  // so the row auto-refreshes when Claude finishes. Stops as soon as no
  // rows have `auto_quoting_pending` = true.
  useEffect(() => {
    const anyPending = items.some((it) => it.auto_quoting_pending);
    if (!anyPending) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [items]);

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    const sub = item.sub_status || (Number(item.amount) > 0 ? "draft" : "unquoted");
    // Hydrate a single job_item from legacy fields if this quote pre-dates the
    // multi-item schema, so the receptionist can price it inline.
    let hydratedItems = item.job_items || [];
    if (!hydratedItems.length && item.job_type) {
      hydratedItems = [{
        ...blankJobItem(item.job_type),
        notes: item.vehicle || item.service || "",
        price: Number(item.amount) || 0,
      }];
    }
    hydratedItems = hydratedItems.map(normalizeItem);
    setForm({ ...blank, ...item, job_items: hydratedItems, sub_status: sub });
    setOpen(true);
  };

  const save = async (e, { requote = false } = {}) => {
    e.preventDefault();
    if (!form.contact_id && !form.contact_name) {
      toast.error("Please select or add a customer first");
      return;
    }
    const items = form.job_items || [];
    // Sum installation prices across all items (new nested schema).
    const itemsSum = items.reduce((s, it) => {
      const insts = it.installations || [];
      return s + insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
              + (insts.length === 0 ? (Number(it.price) || 0) : 0);
    }, 0);
    const override = Number(form.amount_override) || 0;
    const finalAmount = override > 0 ? override : (itemsSum > 0 ? itemsSum : (Number(form.amount) || 0));
    const legacyVehicle = items
      .map((it) => [it.year, it.make, it.model].filter(Boolean).join(" ") || (it.notes || "").split("\n")[0])
      .filter(Boolean)
      .join(" · ");
    const legacyService = Array.from(new Set(items.map((i) => jobTypeMeta(i.job_type).label))).join(" + ");
    // Roll up per-installation `is_estimate` flags into the top-level
    // record flag so the row badge, PDF, and Claude context all still
    // reflect whether ANY figure in the quote is rough. Also sum the
    // installation `price_max` values into `amount_max` so the public
    // PDF / quote page can render the range ("$3,000 - $3,500").
    const anyEstimate = items.some((it) => (it.installations || []).some((inst) => inst?.is_estimate));
    const amountMax = anyEstimate
      ? items.reduce((tot, it) => tot + (it.installations || []).reduce(
          (s, inst) => s + (Number(inst?.price_max) > 0 ? Number(inst.price_max) : Number(inst?.price) || 0),
          0,
        ), 0)
      : 0;
    const payload = {
      ...form,
      job_items: items,
      job_type: items[0]?.job_type || form.job_type || "car",
      vehicle: legacyVehicle || form.vehicle || "",
      service: legacyService || form.service || "",
      amount: finalAmount,
      amount_max: amountMax,
      amount_override: override,
      is_estimate: anyEstimate,
      main_status: "quote",
      sub_status: finalAmount <= 0 ? "unquoted" : (form.sub_status && form.sub_status !== "unquoted" ? form.sub_status : "draft"),
    };
    try {
      let quoteId;
      let priorAmount = 0;
      if (editing) {
        priorAmount = Number(editing.amount) || 0;
        const { data } = await api.put(`/jobs/${editing.id}`, payload);
        quoteId = editing.id;
      } else {
        const { data } = await api.post("/jobs", payload);
        quoteId = data?.id;
      }
      if (requote && quoteId) {
        try {
          await api.post(`/quote-agent/auto-quote/${quoteId}?force=true`);
          toast.success("Saving — AI is re-quoting with your changes");
          // Poll every 3s for up to 60s so we can show the diff the moment
          // Claude finishes. Hands off silently if it takes longer than that.
          let tries = 0;
          const timer = setInterval(async () => {
            tries += 1;
            try {
              const { data: fresh } = await api.get(`/jobs/${quoteId}`);
              if (!fresh?.auto_quoting_pending || tries >= 20) {
                clearInterval(timer);
                const newAmt = Number(fresh?.amount) || 0;
                if (newAmt > 0 && priorAmount > 0 && Math.abs(newAmt - priorAmount) >= 1) {
                  const delta = newAmt - priorAmount;
                  const upDown = delta > 0 ? "▲" : "▼";
                  const cls = delta > 0 ? "text-emerald-400" : "text-red-400";
                  toast(
                    <div className="flex flex-col gap-1" data-testid="requote-diff-toast">
                      <div className="text-slate-100 font-semibold">Requote complete</div>
                      <div className="text-sm text-slate-300 flex items-center gap-2">
                        <span>${priorAmount.toLocaleString()}</span>
                        <span className="text-slate-500">→</span>
                        <span className="text-white font-semibold">${newAmt.toLocaleString()}</span>
                        <span className={`${cls} text-xs font-mono`}>{upDown} ${Math.abs(delta).toLocaleString()}</span>
                      </div>
                    </div>,
                    { duration: 10000 }
                  );
                } else if (newAmt > 0 && priorAmount > 0) {
                  toast.info(`Requote complete — no change ($${newAmt.toLocaleString()})`);
                } else if (fresh?.auto_quote_error) {
                  toast.error("AI couldn't return a priced quote — try Review quote for an interactive session");
                }
                load();
              }
            } catch { /* ignore transient errors */ }
          }, 3000);
        } catch (err) {
          toast.warning("Saved, but couldn't kick off the AI re-quote — try again from the row's Review-quote button");
        }
      } else {
        toast.success(editing ? "Quote updated" : "Quote created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Quote deleted");
    load();
  };

  const duplicate = async (q) => {
    const vehicle = q?.vehicle || q?.contact_name || "this quote";
    if (!window.confirm(`Duplicate the quote for ${vehicle}? A fresh, unquoted copy will appear at the top of the list — handy for a second install on the same vehicle.`)) return;
    try {
      await api.post(`/quotes/${q.id}/duplicate`);
      toast.success("Quote duplicated — edit the new copy to customise the second install");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't duplicate");
    }
  };

  const convertToInvoice = async (q) => {
    if (!q.amount || Number(q.amount) <= 0) {
      toast.error("Add an amount to this quote first — unquoted quotes can't be invoiced.");
      return;
    }
    if (!window.confirm(`Convert quote for ${q.contact_name} into an invoice?`)) return;
    try {
      // Invoices remain in their own collection — pass through the legacy converter
      await api.post(`/quotes/${q.id}/convert-to-invoice`);
      toast.success("Invoice created");
      load();
    } catch (err) {
      // Fallback: create the invoice directly from the unified doc
      try {
        await api.post("/invoices", {
          contact_name: q.contact_name,
          contact_email: q.contact_email || "",
          contact_phone: q.contact_phone || "",
          contact_id: q.contact_id || "",
          quote_id: q.id,
          amount: Number(q.amount) || 0,
          status: "unpaid",
        });
        toast.success("Invoice created");
        load();
      } catch (err2) {
        toast.error(err2.response?.data?.detail || err.response?.data?.detail || "Failed");
      }
    }
  };

  const convertToJob = async (q) => {
    if (!q.amount || Number(q.amount) <= 0) {
      toast.error("Add an amount to this quote first — unquoted quotes can't be scheduled.");
      return;
    }
    setConvertingToJob(q);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-label">Sales</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Quotes</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-1">
              <Search className="w-3 h-3" /> Search
            </label>
            <div className="relative">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="quotes-search"
                placeholder="Name, vehicle, service, amount…"
                className="bg-black/40 border border-white/10 rounded-full pl-9 pr-9 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-white/30 w-[260px]"
              />
              <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  data-testid="quotes-search-clear"
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="relative">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-1">
              <ArrowUpDown className="w-3 h-3" /> Sort
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="quotes-sort"
              className="bg-black/40 border border-white/10 rounded-full px-3 py-2 text-xs text-white focus:outline-none focus:border-white/30 pr-8"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
              ))}
            </select>
          </div>
          <button data-testid="quotes-create-button" onClick={openCreate} className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-100 transition mt-4">
            <Plus className="w-4 h-4" /> New quote
          </button>
        </div>
      </div>

      {/* Archive-match pill — only visible when the user is actively
          searching AND at least one archived record matches. Each of the
          three sections mirrors the Archive tab's tabs so the office
          can tell at a glance whether their customer landed in the
          Completed / Failed / Rejected bucket. Clicking a section jumps
          to the Archive tab with the same search term pre-filled. */}
      {archiveMatches && (
        <div
          data-testid="quotes-archive-pill"
          className="inline-flex items-stretch rounded-full border border-white/10 bg-white/[0.03] overflow-hidden text-xs"
        >
          <span className="flex items-center gap-1.5 px-3 text-[10px] uppercase tracking-widest text-slate-400 border-r border-white/10">
            <ArchiveIcon className="w-3 h-3" /> Also in archive
          </span>
          {ARCHIVE_GROUPS.map((g, i) => {
            const Icon = g.icon;
            const count = archiveMatches[g.key] || 0;
            const dim = count === 0;
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => navigate(`/archive?group=${g.key}&q=${encodeURIComponent(query.trim())}`)}
                disabled={dim}
                data-testid={`quotes-archive-pill-${g.key}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 transition ${
                  dim
                    ? "text-slate-600 cursor-default"
                    : "text-white hover:bg-white/10"
                } ${i > 0 ? "border-l border-white/10" : ""}`}
                title={
                  dim
                    ? `No ${g.label.toLowerCase()} match "${query}"`
                    : `${count} ${g.label.toLowerCase()} match "${query}" — click to open the Archive`
                }
              >
                <Icon className="w-3 h-3" />
                <span className="text-[11px]">{g.label}</span>
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${
                  dim ? "bg-white/5 text-slate-600" : "bg-emerald-500/20 text-emerald-200"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <GlassPanel className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="quotes-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/5 text-left">
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Vehicle</th>
                <th className="px-5 py-4">Service</th>
                <th className="px-5 py-4">Service type</th>
                <th className="px-5 py-4">Follow-up</th>
                <th className="px-5 py-4">Amount</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500 italic">
                  {query ? `No quotes match "${query}".` : "No quotes yet."}
                </td></tr>
              )}
              {sortedItems.map((it) => {
                const isUnquoted = !it.amount || Number(it.amount) <= 0;
                const sub = it.sub_status || (isUnquoted ? "unquoted" : "draft");
                const isAiPending = !!(it.auto_quoted_at && !it.auto_quote_approved_at
                  && Number(it.auto_quoted_amount || 0) > 0);
                const aiProposedAmount = Number(it.auto_quoted_amount || 0);
                return (
                  <React.Fragment key={it.id}>
                    {/* Stage row — full-width visual pipeline at the top of the record */}
                    <tr
                      data-testid={`quote-stage-row-${it.id}`}
                      data-quote-row-id={it.id}
                      className="bg-white/[0.02] border-t border-white/5 transition-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <td colSpan={7} className="px-5 pt-3 pb-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0">Status</span>
                          {isUnquoted ? (
                            <span className="pill pill-error shrink-0" data-testid={`quote-unquoted-${it.id}`}>UNQUOTED</span>
                          ) : (
                            <span data-testid={`quote-stage-pill-${it.id}`} className={`pill ${STATUS_PILL[sub] || "pill-muted"} shrink-0`}>{sub}</span>
                          )}
                          {it.auto_quoting_pending && (
                            <span
                              data-testid={`quote-auto-pending-${it.id}`}
                              className="pill pill-info shrink-0 gap-1 animate-pulse"
                              title="Claude is auto-quoting this job — reload in a moment"
                            >
                              <Bot className="w-2.5 h-2.5" /> AI quoting…
                            </span>
                          )}
                          {it.auto_quote_error && !it.auto_quoting_pending && (
                            <span
                              data-testid={`quote-auto-error-${it.id}`}
                              className="pill pill-warning shrink-0 gap-1"
                              title={it.auto_quote_error}
                            >
                              <Bot className="w-2.5 h-2.5" /> Auto-quote failed
                            </span>
                          )}
                          {Number(it.auto_quote_confidence) > 0 && (
                            <span
                              data-testid={`quote-confidence-${it.id}`}
                              className={`pill shrink-0 gap-1 text-[10px] uppercase tracking-wider ${
                                Number(it.auto_quote_confidence) >= 4 ? "pill-success"
                                : Number(it.auto_quote_confidence) >= 3 ? "pill-info"
                                : "pill-warning"
                              }`}
                              title={`AI confidence in this quote: ${it.auto_quote_confidence}/5`}
                            >
                              <Bot className="w-2.5 h-2.5" /> {it.auto_quote_confidence}/5
                            </span>
                          )}
                          {it.duplicated_from && (
                            <span
                              data-testid={`quote-duplicated-${it.id}`}
                              className="pill shrink-0 gap-1 bg-sky-500/15 text-sky-200 border border-sky-400/40 text-[10px] uppercase tracking-wider"
                              title={`Duplicated from quote #${String(it.duplicated_from).slice(0, 8)} — click to jump to the original`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const el = document.querySelector(`[data-quote-row-id="${it.duplicated_from}"]`);
                                if (el) {
                                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                                  el.classList.add("ring-2", "ring-sky-400/70");
                                  setTimeout(() => el.classList.remove("ring-2", "ring-sky-400/70"), 1800);
                                } else {
                                  toast.info("Original quote isn't in the current list — try clearing filters.");
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <Copy className="w-2.5 h-2.5" /> Duplicated
                            </span>
                          )}
                          <FollowUpPill quote={it} onDone={load} />
                          <div className="flex-1 min-w-[240px]">
                            <StatusPipeline job={it} onChange={load} compact inline hideMain />
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Details row */}
                    <tr
                      data-testid={`quote-row-${it.id}`}
                      className="border-b border-white/10 hover:bg-white/[0.03] cursor-pointer align-top"
                      onClick={() => setDetailsFor(it)}
                    >
                      <td className="px-5 pt-1 pb-3 text-white">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{it.contact_name}</span>
                          {(() => {
                            const days = quoteExpiryDaysLeft(it);
                            if (days === null) return null;
                            // Hide once the quote is accepted (customer has
                            // committed) — otherwise show the countdown so
                            // the office can see which quotes need a chase.
                            if (it.status === "accepted") return null;
                            const expired = days < 0;
                            const soon = !expired && days <= 7;
                            const label = expired
                              ? `Expired ${Math.abs(days)}d ago`
                              : days === 0
                                ? "Expires today"
                                : `Expires in ${days}d`;
                            return (
                              <span
                                data-testid={`quote-expiry-${it.id}`}
                                title={`Quotes expire ${QUOTE_EXPIRY_DAYS} days after arrival`}
                                className={[
                                  "inline-flex items-center gap-1 pill text-[9px] uppercase tracking-wider",
                                  expired
                                    ? "pill-error"
                                    : soon
                                      ? "pill-warning"
                                      : "pill-muted",
                                ].join(" ")}
                              >
                                <Clock3 className="w-2.5 h-2.5" /> {label}
                              </span>
                            );
                          })()}
                          {it.is_estimate && (
                            <span
                              data-testid={`quote-estimate-badge-${it.id}`}
                              title="This quote is a rough estimate — pricing may change once specifics are confirmed."
                              className="inline-flex items-center gap-1 pill pill-warning text-[9px] uppercase tracking-wider"
                            >
                              <Info className="w-2.5 h-2.5" /> Estimate
                            </span>
                          )}
                          {it.customer_accepted_at && (it.declined_installations || []).length > 0 && (
                            <span
                              data-testid={`quote-declined-options-${it.id}`}
                              title={`Customer declined: ${(it.declined_installations || []).map((d) => `${(d.installation_type || "").replace(/_/g, " ")}${d.price ? ` ($${Number(d.price).toLocaleString()})` : ""}`).join(", ")}`}
                              className="pill pill-muted text-[9px] uppercase tracking-wider"
                            >
                              {(it.declined_installations || []).length} option{(it.declined_installations || []).length === 1 ? "" : "s"} declined
                            </span>
                          )}
                          {it.customer_accepted_at && (
                            <button
                              type="button"
                              data-testid={`quote-customer-accepted-${it.id}`}
                              title={
                                (it.customer_preferred_dates || []).length
                                  ? `Customer accepted. Preferred install dates: ${(it.customer_preferred_dates || []).join(", ")}. Click to book.`
                                  : "Customer accepted this quote — click to book the install"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                convertToJob(it);
                              }}
                              className="inline-flex items-center gap-1.5 pill pill-success text-[10px] uppercase tracking-wider font-semibold border-2 border-emerald-400/60 bg-emerald-500/20 hover:bg-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.35)] animate-[pulse_2s_ease-in-out_infinite] cursor-pointer transition"
                            >
                              <Check className="w-3 h-3" />
                              Accepted — Book Install
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-5 pt-1 pb-3 text-slate-300">
                        <div className="flex items-center gap-2">
                          {it.photo_path && (
                            <CustomerVehiclePhoto path={it.photo_path} size="sm" />
                          )}
                          <span className="flex-1">{it.vehicle}</span>
                        </div>
                      </td>
                      <td className="px-5 pt-1 pb-3 text-slate-300">{it.service}</td>
                      <td className="px-5 pt-1 pb-3 text-slate-300 max-w-[240px]" data-testid={`quote-service-types-${it.id}`}>
                        {(() => {
                          const lbl = serviceTypesLabel(it);
                          return lbl
                            ? <span className="text-xs leading-snug block">{lbl}</span>
                            : <span className="text-slate-500 italic text-xs">—</span>;
                        })()}
                      </td>
                      <td className="px-5 pt-1 pb-3" onClick={(e) => e.stopPropagation()} data-testid={`quote-followup-cell-${it.id}`}>
                        <QuoteFollowUpCell quote={it} onSaved={load} />
                      </td>
                      <td className="px-5 pt-1 pb-3 text-white font-medium">
                        {(() => {
                          const hasManual = Number(it.amount) > 0;
                          if (isAiPending && hasManual) {
                            // Manual override entered while AI proposal is
                            // still pending — stack both so the office can
                            // eyeball the delta before approving.
                            return (
                              <div
                                data-testid={`quote-amount-stack-${it.id}`}
                                className="flex flex-col leading-tight"
                              >
                                <span className="inline-flex items-center gap-1.5 text-violet-200 text-sm"
                                  title="AI-proposed — not yet applied.">
                                  <Bot className="w-3 h-3" />
                                  ${aiProposedAmount.toLocaleString()}
                                  <span className="text-[10px] uppercase tracking-wider text-violet-400/80">AI</span>
                                </span>
                                <span
                                  data-testid={`quote-amount-manual-${it.id}`}
                                  className="inline-flex items-center gap-1 text-white text-base font-semibold mt-0.5"
                                  title="Manual amount entered by the office"
                                >
                                  ${Number(it.amount).toLocaleString()}
                                  <span className="text-[10px] uppercase tracking-wider text-slate-500">Manual</span>
                                </span>
                              </div>
                            );
                          }
                          if (isAiPending) {
                            return (
                              <span
                                data-testid={`quote-amount-ai-${it.id}`}
                                className="inline-flex items-center gap-1.5 text-violet-200"
                                title="AI-proposed — not yet applied. Click Review quote to approve."
                              >
                                <Bot className="w-3 h-3" />
                                ${aiProposedAmount.toLocaleString()}
                                <span className="text-[10px] uppercase tracking-wider text-violet-400/80">AI</span>
                              </span>
                            );
                          }
                          if (isUnquoted) return <span className="text-slate-500 italic">—</span>;
                          return `$${Number(it.amount).toLocaleString()}`;
                        })()}
                      </td>
                      <td className="px-5 pt-1 pb-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex gap-1.5 flex-wrap justify-end">
                          {isUnquoted ? (
                            <>
                              {isAiPending ? (
                                <button
                                  data-testid={`quote-review-${it.id}`}
                                  onClick={() => setReviewing(it)}
                                  title="Review the AI-proposed price + calculations, then approve or override"
                                  className="p-1.5 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 text-violet-300 flex items-center gap-1 text-xs px-2"
                                ><Bot className="w-3 h-3" /> Review quote</button>
                              ) : !isProPlan ? (
                                <button
                                  data-testid={`quote-auto-upgrade-${it.id}`}
                                  onClick={() => {
                                    toast("Automatic AI Quoting is a Pro feature", {
                                      description: "Upgrade to WrapConsole Pro to let Claude draft every quote for you.",
                                      action: {
                                        label: "Upgrade",
                                        onClick: () => navigate("/billing"),
                                      },
                                    });
                                  }}
                                  title="Automatic AI Quoting is included on WrapConsole Pro — click to upgrade"
                                  className="p-1.5 rounded-lg bg-violet-500/5 hover:bg-violet-500/15 text-violet-300/80 flex items-center gap-1 text-xs px-2 border border-violet-400/20"
                                ><Lock className="w-3 h-3" /> AI quote · Pro</button>
                              ) : (
                                <button
                                  data-testid={`quote-auto-${it.id}`}
                                  onClick={async () => {
                                    // Fire the same behind-the-scenes auto-quote
                                    // sweep the periodic system runs. `force=true`
                                    // lets office staff re-run on a quote that
                                    // already has an AI attempt if inputs changed.
                                    try {
                                      await api.post(`/quote-agent/auto-quote/${it.id}?force=true`);
                                      toast.success("AI is quoting behind the scenes…");
                                      // Optimistically mark this row as pending so
                                      // the button flips to "AI quoting…" and the
                                      // list polls itself every 3s until Claude
                                      // finishes and the pill goes green.
                                      setItems((prev) => prev.map((q) => q.id === it.id ? { ...q, auto_quoting_pending: true } : q));
                                    } catch (e) {
                                      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't kick off auto-quote");
                                    }
                                  }}
                                  disabled={it.auto_quoting_pending}
                                  title={it.auto_quoting_pending ? "AI is auto-quoting this job — one moment" : "Run Automatic AI Quoting behind the scenes"}
                                  className="p-1.5 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 text-violet-300 flex items-center gap-1 text-xs px-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                ><Bot className="w-3 h-3" /> {it.auto_quoting_pending ? "AI quoting…" : "Auto quote"}</button>
                              )}
                              <button
                                data-testid={`quote-manual-${it.id}`}
                                onClick={() => openEdit(it)}
                                title="Enter the amount yourself"
                                className="p-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 flex items-center gap-1 text-xs px-2"
                              ><Edit3 className="w-3 h-3" /> Manual</button>
                            </>
                          ) : (
                            <>
                              {it.auto_quoted_at && (
                                <button
                                  data-testid={`quote-review-${it.id}`}
                                  onClick={() => setReviewing(it)}
                                  title="Review the AI-generated quote and pricing calculations"
                                  className="p-1.5 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 text-violet-300 flex items-center gap-1 text-xs px-2"
                                ><Bot className="w-3 h-3" /> Review quote</button>
                              )}
                              <button data-testid={`quote-send-${it.id}`} onClick={() => setSending(it)} title="Send to customer" className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 flex items-center gap-1 text-xs px-2"><Send className="w-3 h-3" /> Send</button>
                              <button
                                data-testid={`quote-to-invoice-${it.id}`}
                                onClick={() => convertToInvoice(it)}
                                title="Convert to invoice"
                                className="p-1.5 rounded-lg flex items-center gap-1 text-xs px-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300"
                              ><ArrowRight className="w-3 h-3" /> Invoice</button>
                              <button
                                data-testid={`quote-to-job-${it.id}`}
                                onClick={() => convertToJob(it)}
                                title="Convert to job"
                                className="p-1.5 rounded-lg flex items-center gap-1 text-xs px-2 bg-violet-500/10 hover:bg-violet-500/20 text-violet-300"
                              ><Wrench className="w-3 h-3" /> Job</button>
                            </>
                          )}
                          <button
                            data-testid={`quote-duplicate-${it.id}`}
                            onClick={() => duplicate(it)}
                            title="Duplicate this quote — useful for a second install on the same vehicle"
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300"
                          ><Copy className="w-3.5 h-3.5" /></button>
                          <button data-testid={`quote-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`quote-delete-${it.id}`} onClick={() => remove(it.id)} className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassPanel>

      {open && (
        <FormModal
          title={editing ? "Edit quote" : "New quote"}
          onClose={() => setOpen(false)}
          onSubmit={save}
          extraActions={editing ? (
            <button
              type="button"
              data-testid="quote-save-and-requote"
              onClick={(e) => save(e, { requote: true })}
              className="px-5 py-2.5 rounded-full bg-violet-500 text-white text-sm font-semibold hover:bg-violet-400 flex items-center gap-1.5"
              title="Save your changes and rerun the AI quoting agent with the updated details"
            >
              <Bot className="w-3.5 h-3.5" /> Save &amp; requote
            </button>
          ) : null}
        >
          <ContactPickerField
            value={form.contact_id}
            required
            testId="quote-customer"
            label="Customer"
            onChange={(c) => {
              if (!c) {
                setForm({ ...form, contact_id: "", contact_name: "", contact_email: "", contact_phone: "", contact_address: "" });
                return;
              }
              setForm({
                ...form,
                contact_id: c.id,
                contact_name: c.name || "",
                contact_email: c.email || "",
                contact_phone: c.phone || "",
                contact_address: c.address || "",
                vehicle: form.vehicle || c.vehicle || "",
              });
            }}
          />

          <JobTypeMultiSelect
            items={form.job_items || []}
            onChange={(items) => setForm({ ...form, job_items: items })}
            testId="quote-job-types"
          />

          {(form.job_items || []).length > 0 && (
            <div className="space-y-3">
              {form.job_items.map((it, idx) => (
                <JobItemForm
                  key={it.id}
                  item={it}
                  index={idx}
                  showQuoteSeparately={true}
                  onPatch={(patch) => setForm({
                    ...form,
                    job_items: form.job_items.map((x, i) => i === idx ? { ...x, ...patch } : x),
                  })}
                  onRemove={() => setForm({
                    ...form,
                    job_items: form.job_items.filter((_, i) => i !== idx),
                  })}
                />
              ))}
            </div>
          )}

          {/* Auto-sum + override total */}
          {(form.job_items || []).length > 0 && (() => {
            const sum = form.job_items.reduce((s, it) => {
              const insts = it.installations || [];
              return s + insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
                      + (insts.length === 0 ? (Number(it.price) || 0) : 0);
            }, 0);
            return (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Items auto-total</div>
                  <div className="text-white font-heading text-lg">${sum.toLocaleString()}</div>
                </div>
                <div>
                  <label className="text-label block mb-1.5">Manual override (leave blank / 0 to use auto-sum)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.amount_override || ""}
                    onChange={(e) => setForm({ ...form, amount_override: e.target.value })}
                    placeholder="e.g. 4200 for a bundle discount"
                    data-testid="quote-amount-override"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
                  />
                </div>
              </div>
            );
          })()}

          <SelectField
            label="Status"
            value={form.sub_status}
            onChange={(v) => setForm({ ...form, sub_status: v })}
            options={SUB_STATUSES.quote.map((s) => ({ value: s.value, label: s.label }))}
            testId="quote-status"
          />

          {/* Estimation flag moved to per-installation on the job items
              below — each installation now has its own "Estimate Price"
              tick with an optional range max, right under the $ input. */}

          <div>
            <label className="text-label block mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Install location</label>
            <div className="grid grid-cols-2 gap-2">
              {LOCATION_TYPES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  data-testid={`quote-location-${l.value}`}
                  onClick={() => setForm({ ...form, location_type: l.value })}
                  className={`text-sm rounded-2xl border py-2 transition ${form.location_type === l.value ? "bg-white text-black border-white font-semibold" : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          {form.location_type === "off_site" && (
            <Field label="Off-site address" value={form.location_address} onChange={(v) => setForm({ ...form, location_address: v })} testId="quote-address" />
          )}

          {/* Customer-facing install duration — shown on the public
              quote page, the quote PDF, and the quote email. Kept free-
              text so office can match the language they already use with
              customers ("2 business days", "4-6 hours", "overnight"). */}
          <Field
            label="Install time (shown to customer)"
            value={form.customer_install_duration}
            onChange={(v) => setForm({ ...form, customer_install_duration: v })}
            placeholder="e.g. 2 business days, 4-6 hours, overnight"
            testId="quote-customer-install-duration"
            hint="This appears on the customer's quote email + PDF — separate from your internal per-installation hours."
          />

          <TextArea label="Internal Shop Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="quote-notes" hint="Internal only — customers won't see these notes on their quote." />
        </FormModal>
      )}

      {sending && <SendQuoteModal quote={sending} onClose={() => { setSending(null); load(); }} onSplit={load} />}
      {reviewing && (
        <QuoteReviewModal
          quote={reviewing}
          onClose={() => setReviewing(null)}
          onApproved={load}
        />
      )}
      {convertingToJob && (
        <ConvertToJobModal
          quote={convertingToJob}
          onClose={() => setConvertingToJob(null)}
          onCreated={load}
        />
      )}
      {detailsFor && (
        <DetailsModal kind="quote" record={detailsFor} onClose={() => setDetailsFor(null)} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* QuoteFollowUpCell                                                          */
/* -------------------------------------------------------------------------- */
/**
 * Manual follow-up scheduler for quotes. Complements the auto 3/7/14-day
 * drip: this is for cases where the customer explicitly says "I need six
 * weeks to save, come back to me then". The office sets a date + optional
 * note; the row goes amber the day the date arrives so nobody forgets.
 *
 * Persists to `manual_follow_up_at` + `manual_follow_up_note` on the
 * quote doc via `PUT /jobs/{id}`.
 *
 * Also pauses the auto follow-up sequence while a future manual date is
 * set — customers who've asked to wait shouldn't get a "checking in on
 * day 3" email while their manual date is still weeks away.
 */
function QuoteFollowUpCell({ quote, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [date, setDate] = React.useState(quote.manual_follow_up_at || "");
  const [note, setNote] = React.useState(quote.manual_follow_up_note || "");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setDate(quote.manual_follow_up_at || "");
    setNote(quote.manual_follow_up_note || "");
  }, [quote.manual_follow_up_at, quote.manual_follow_up_note, quote.id]);

  const scheduled = quote.manual_follow_up_at || "";
  const todayYmd = new Date().toISOString().slice(0, 10);
  const isDue = scheduled && scheduled <= todayYmd;
  const isFuture = scheduled && scheduled > todayYmd;

  const daysAway = React.useMemo(() => {
    if (!scheduled) return null;
    const target = new Date(scheduled + "T00:00:00");
    const today = new Date(todayYmd + "T00:00:00");
    return Math.round((target - today) / 86400000);
  }, [scheduled, todayYmd]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/jobs/${quote.id}`, {
        ...quote,
        manual_follow_up_at: date || "",
        manual_follow_up_note: date ? note : "",
      });
      toast.success(date ? `Follow-up set for ${date}` : "Follow-up cleared");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setDate("");
    setNote("");
    try {
      await api.put(`/jobs/${quote.id}`, {
        ...quote,
        manual_follow_up_at: "",
        manual_follow_up_note: "",
      });
      toast.success("Follow-up cleared");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    if (!scheduled) {
      return (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`quote-followup-add-${quote.id}`}
          className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-white px-2 py-1 rounded-full border border-dashed border-white/10 hover:border-white/25 transition"
        >
          <CalendarClock className="w-3 h-3" /> Set follow-up
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`quote-followup-pill-${quote.id}`}
        title={quote.manual_follow_up_note || `Follow up on ${scheduled}`}
        className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full transition ${
          isDue
            ? "bg-amber-500/20 border border-amber-400/60 text-amber-900 dark:text-amber-100 hover:bg-amber-500/30 animate-pulse"
            : "bg-white/5 border border-white/10 text-slate-200 hover:border-white/25"
        }`}
      >
        <CalendarClock className="w-3 h-3" />
        <span>{scheduled}</span>
        {isDue && <span className="font-semibold">· chase now</span>}
        {isFuture && daysAway != null && (
          <span className="text-slate-400 dark:text-slate-500">
            · in {daysAway}d
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      data-testid={`quote-followup-editor-${quote.id}`}
      className="inline-flex flex-col gap-1.5 min-w-[220px]"
    >
      <div className="flex items-center gap-1.5">
        <input
          type="date"
          value={date}
          min={todayYmd}
          onChange={(e) => setDate(e.target.value)}
          data-testid={`quote-followup-date-${quote.id}`}
          className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-white/30 flex-1"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`quote-followup-save-${quote.id}`}
          className="p-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 disabled:opacity-50"
          title="Save"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          data-testid={`quote-followup-cancel-${quote.id}`}
          className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400"
          title="Cancel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Reason (e.g. saving up)"
        data-testid={`quote-followup-note-${quote.id}`}
        className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
      />
      {scheduled && (
        <button
          type="button"
          onClick={clear}
          disabled={saving}
          data-testid={`quote-followup-clear-${quote.id}`}
          className="text-[10px] text-slate-500 hover:text-red-300 self-start"
        >
          Clear follow-up
        </button>
      )}
    </div>
  );
}

